import { useCallback } from 'react';
import { OnboardingFormFields } from '@Types/index';
import { useBoundStore } from '@Store/index';
import { getStateDropdownItem } from './AddressStateUtil';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { Country } from '@Types/country';

interface IProps<T extends FieldValues> {
  state: string | undefined;
  line1: string | undefined;
  line2: string | undefined;
  city: string | undefined;
  country?: Country | null | undefined;
  postalCode: string | undefined;
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
  formMethods: ExtendedUseFormReturn<T>;
  defaultValues: T;
  validStates?: { title: string; value: string }[];
}

export const useHandleOnCheckedChange = <T extends FieldValues>({
  state,
  line1,
  line2,
  city,
  postalCode,
  setValue,
  trigger,
  formMethods,
  country,
  defaultValues: defValues,
  validStates,
}: IProps<T>) => {
  const resetFormFieldErrors = useBoundStore((s) => s.errorSlice.resetFormFieldErrors);
  const handleOnCheckChange = useCallback(
    (isChecked: boolean) => {
      const currentState = getStateDropdownItem(state, country, validStates);
      const triggerValidationInAddress = () => {
        trigger(OnboardingFormFields.LINE1 as Path<T>);
        trigger(OnboardingFormFields.APARTMENT as Path<T>);
        trigger(OnboardingFormFields.CITY as Path<T>);
        trigger(OnboardingFormFields.STATE as Path<T>);
        trigger(OnboardingFormFields.ZIP as Path<T>);
        trigger(OnboardingFormFields.COUNTRY as Path<T>);
      };

      if (isChecked) {
        resetFormFieldErrors();
        formMethods.clearErrors();
        setValue(OnboardingFormFields.STATE as Path<T>, currentState as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.LINE1 as Path<T>, line1 as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.CITY as Path<T>, city as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.ZIP as Path<T>, postalCode as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.APARTMENT as Path<T>, line2 as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.COUNTRY as Path<T>, country as PathValue<T, Path<T>>);
        triggerValidationInAddress();
      } else {
        const enrichedCountryObject = defValues[OnboardingFormFields.COUNTRY]
          ? {
              ...defValues[OnboardingFormFields.COUNTRY],
              title: defValues[OnboardingFormFields.COUNTRY]?.name,
              value: defValues[OnboardingFormFields.COUNTRY]?.code,
            }
          : null;
        const defaultValues: Partial<Record<keyof typeof OnboardingFormFields, string>> = {
          [OnboardingFormFields.LINE1]: defValues[OnboardingFormFields.LINE1],
          [OnboardingFormFields.APARTMENT]: defValues[OnboardingFormFields.APARTMENT],
          [OnboardingFormFields.CITY]: defValues[OnboardingFormFields.CITY],
          [OnboardingFormFields.STATE]: defValues[OnboardingFormFields.STATE],
          [OnboardingFormFields.ZIP]: defValues[OnboardingFormFields.ZIP],
          [OnboardingFormFields.COUNTRY]: enrichedCountryObject,
        };
        Object.keys(defaultValues).forEach((key) => {
          formMethods.resetField(key as Path<T>, {
            defaultValue: defaultValues[key as keyof typeof OnboardingFormFields] as PathValue<
              T,
              Path<T>
            >,
          });
        });
        if (defValues[OnboardingFormFields.COUNTRY] == null) {
          setValue(OnboardingFormFields.COUNTRY as Path<T>, undefined as PathValue<T, Path<T>>);
        }
        if (defValues[OnboardingFormFields.COUNTRY] == null) {
          setValue(OnboardingFormFields.COUNTRY as Path<T>, null as PathValue<T, Path<T>>);
        }
        if (defValues[OnboardingFormFields.COUNTRY] == null) {
          setValue(OnboardingFormFields.COUNTRY as Path<T>, null as PathValue<T, Path<T>>);
        }
        if (defValues[OnboardingFormFields.COUNTRY] == null) {
          formMethods.resetField(OnboardingFormFields.COUNTRY as Path<T>, {
            defaultValue: undefined as PathValue<T, Path<T>>,
          });
        }
      }
    },
    [
      city,
      line1,
      line2,
      postalCode,
      setValue,
      state,
      trigger,
      formMethods,
      defValues,
      country,
      validStates,
      resetFormFieldErrors,
    ]
  );
  return { handleOnCheckChange };
};
